import { PalmettoButton, PalmettoIcon, PalmettoTextInput } from '@palmetto/dsm-components';
import { useCallback, useMemo } from 'react';

interface ProductLineValuesEditorProps {
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  errorMessage?: string;
}

const addIcon = <PalmettoIcon name="add" size="sm" />;

interface ProductLineValueRowProps {
  index: number;
  value: string;
  disabled?: boolean;
  onValueChange: (index: number, value: string) => void;
  onRemove: (index: number) => void;
}

const ProductLineValueRow = ({ index, value, disabled, onValueChange, onRemove }: ProductLineValueRowProps) => {
  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => onValueChange(index, e.target.value),
    [onValueChange, index],
  );

  const handleRemove = useCallback(() => onRemove(index), [onRemove, index]);

  return (
    <div className="flex items-end gap-8">
      <div className="flex-auto">
        <PalmettoTextInput
          id={`product-line-value-${index}`}
          label={`Value ${index + 1}`}
          placeholder="e.g. Powerwall 3"
          value={value}
          onChange={handleChange}
          disabled={disabled}
        />
      </div>
      <PalmettoButton
        type="button"
        emphasis="secondary"
        layout="hug"
        aria-label={`Remove value ${index + 1}`}
        onClick={handleRemove}
        disabled={disabled}
      >
        <PalmettoIcon name="trash" size="sm" />
      </PalmettoButton>
    </div>
  );
};

/**
 * Mirrors the raw rows the user typed; trimming and dropping blank rows happen on submit via
 * `cleanProductLineValues`. Matching and de-duplication are the server's job.
 */
export const ProductLineValuesEditor = ({
  value,
  onChange,
  disabled,
  errorMessage,
}: ProductLineValuesEditorProps) => {
  const rows = useMemo(() => (value.length > 0 ? value : ['']), [value]);

  const handleValueChange = useCallback(
    (index: number, nextValue: string) => onChange(rows.map((row, i) => (i === index ? nextValue : row))),
    [rows, onChange],
  );

  const handleAdd = useCallback(() => onChange([...rows, '']), [rows, onChange]);

  const handleRemove = useCallback(
    (index: number) => {
      const next = rows.filter((_, i) => i !== index);
      onChange(next.length > 0 ? next : ['']);
    },
    [rows, onChange],
  );

  return (
    <div className="flex flex-col gap-8">
      <div className="body-bold-300 text-text-primary">Product Catalog values</div>
      <div className="body-200 text-text-secondary">
        Each value is matched against the catalog <code>product-line</code> attribute, ignoring case and extra
        spaces. Add every spelling the catalog uses.
      </div>
      {rows.map((row, index) => (
        <ProductLineValueRow
          key={index}
          index={index}
          value={row}
          disabled={disabled}
          onValueChange={handleValueChange}
          onRemove={handleRemove}
        />
      ))}
      <div>
        <PalmettoButton
          type="button"
          emphasis="secondary"
          layout="hug"
          leadingIcon={addIcon}
          onClick={handleAdd}
          disabled={disabled}
        >
          Add Value
        </PalmettoButton>
      </div>
      {errorMessage && (
        <div role="alert" className="body-200 text-text-error">
          {errorMessage}
        </div>
      )}
    </div>
  );
};

export const atLeastOneValueMessage = 'At least one value is required';

export const cleanProductLineValues = (rows: string[]): string[] =>
  rows.map((row) => row.trim()).filter((row) => row.length > 0);
